(() => {

class HandleControl extends HTMLElement {
  constructor() {
    super();
    this._isDragging = false;
    this._dragStartX = 0;
  }

  connectedCallback() {
    this.pill = this.querySelector('.handle__pill');
    this.halo = this.querySelector('.handle__halo');
    this.leftBtn = this.querySelector('.handle__btn--left');
    this.rightBtn = this.querySelector('.handle__btn--right');
    this.drag = this.querySelector('.handle__drag');

    if (!this.pill) return;

    this._bindHalo();
    this._bindButtons();
    this._bindDrag();
  }

  _bindHalo() {
    this._onPillEnter = () => {
      this.halo.classList.add('is-visible');
    };

    this._onPillMove = (e) => {
      const rect = this.pill.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const w = rect.width;
      const inset = 4;
      const leftEnd = this.leftBtn.offsetWidth;
      const rightStart = w - this.rightBtn.offsetWidth;
      let snapX;

      if (x < leftEnd) {
        snapX = (leftEnd - inset) / 2 + 2;
      } else if (x > rightStart) {
        snapX = (rightStart - inset) + (w - inset - rightStart) / 2 - 2;
      } else {
        snapX = (leftEnd - inset) + (rightStart - leftEnd) / 2;
      }

      this.halo.style.setProperty('--halo-x', `${snapX}px`);
    };

    this._onPillLeave = () => {
      if (!this._isDragging) {
        this.halo.classList.remove('is-visible');
      }
    };

    this.pill.addEventListener('mouseenter', this._onPillEnter);
    this.pill.addEventListener('mousemove', this._onPillMove);
    this.pill.addEventListener('mouseleave', this._onPillLeave);
  }

  _bindButtons() {
    this.leftBtn.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('handle:left', { bubbles: true }));
    });

    this.rightBtn.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('handle:right', { bubbles: true }));
    });
  }

  _bindDrag() {
    this.drag.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this._onDragStart(e);
    });

    this.drag.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this._onDragStart(e);
    }, { passive: false });

    this._onDragMoveBound = (e) => this._onDragMove(e);
    this._onDragEndBound = () => this._onDragEnd();

    document.addEventListener('mousemove', this._onDragMoveBound);
    document.addEventListener('touchmove', this._onDragMoveBound, { passive: true });
    document.addEventListener('mouseup', this._onDragEndBound);
    document.addEventListener('touchend', this._onDragEndBound);
  }

  _onDragStart(e) {
    this._isDragging = true;
    this._dragStartX = (e.clientX !== undefined) ? e.clientX : e.touches[0].clientX;
    this.drag.classList.add('is-dragging');
    this.pill.classList.remove('is-returning');
    if (this.halo) this.halo.classList.add('is-visible');

    this.dispatchEvent(new CustomEvent('handle:dragstart', {
      bubbles: true,
      detail: { clientX: this._dragStartX }
    }));
  }

  _onDragMove(e) {
    if (!this._isDragging) return;

    let currentX;
    if (e.clientX !== undefined) {
      currentX = e.clientX;
    } else if (e.touches && e.touches.length > 0) {
      currentX = e.touches[0].clientX;
    } else {
      return;
    }

    const displacement = currentX - this._dragStartX;
    this.pill.style.setProperty('--pill-offset', `${displacement}px`);

    this.dispatchEvent(new CustomEvent('handle:dragmove', {
      bubbles: true,
      detail: { clientX: currentX, displacement }
    }));
  }

  _onDragEnd() {
    if (!this._isDragging) return;
    this._isDragging = false;
    this.drag.classList.remove('is-dragging');
    this.pill.classList.add('is-returning');
    this.pill.style.setProperty('--pill-offset', '0px');
    if (this.halo) this.halo.classList.remove('is-visible');

    this.pill.addEventListener('transitionend', () => {
      this.pill.classList.remove('is-returning');
    }, { once: true });

    this.dispatchEvent(new CustomEvent('handle:dragend', { bubbles: true }));
  }

  disconnectedCallback() {
    if (this.pill) {
      this.pill.removeEventListener('mouseenter', this._onPillEnter);
      this.pill.removeEventListener('mousemove', this._onPillMove);
      this.pill.removeEventListener('mouseleave', this._onPillLeave);
    }
    document.removeEventListener('mousemove', this._onDragMoveBound);
    document.removeEventListener('touchmove', this._onDragMoveBound);
    document.removeEventListener('mouseup', this._onDragEndBound);
    document.removeEventListener('touchend', this._onDragEndBound);
  }
}

if (!customElements.get('handle-control')) {
  customElements.define('handle-control', HandleControl);
}

})();
